/**
 * Git Server Manager
 *
 * Admin interface for registering, editing, and deleting git servers.
 * The registration form lives in `GitServerRegistrationForm` to keep
 * this component under the 200-line cap.
 */

import { type ReactElement } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gitApi } from '../../git/api/gitApi';
import { useGitServers } from '../../git/hooks/useGitServers';
import { GitServerLoginProviderSelect } from './GitServerLoginProviderSelect';
import { GitServerRegistrationForm } from './GitServerRegistrationForm';

function extractErrorDetail(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === 'string') return detail;
  }
  return null;
}

export function GitServerManager(): ReactElement {
  const { data, isLoading } = useGitServers();
  const qc = useQueryClient();

  const update = useMutation({
    mutationFn: (vars: { id: string; is_active?: boolean }) =>
      gitApi.updateServer(vars.id, { is_active: vars.is_active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gitServers'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => gitApi.deleteServer(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gitServers'] });
    },
  });

  const onDelete = (id: string, name: string): void => {
    const ok = window.confirm(
      `Delete git server "${name}"? Any per-user credentials for it will also be removed. ` +
        `Will fail if cached projects reference this server.`,
    );
    if (ok) remove.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="settings-loading">Loading servers…</div>
    );
  }

  const servers = data?.servers ?? [];

  return (
    <>
      <p className="admin-page__subtitle">
        Register and manage git servers that developers can use for version control.
      </p>

      {servers.length > 0 && (
        <section className="git-server-manager__table-section">
          <table className="feature-table__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>URL</th>
                <th>Active</th>
                <th>Service Token</th>
                <th>Git Login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {servers.map((s) => (
                <tr key={s.id}>
                  <td>{s.display_name}</td>
                  <td>{s.provider_type}</td>
                  <td className="git-server-manager__url">{s.base_url}</td>
                  <td>{s.is_active ? '✓' : '—'}</td>
                  <td>{s.has_service_account_token ? '✓' : '—'}</td>
                  <td>
                    <GitServerLoginProviderSelect server={s} />
                  </td>
                  <td>
                    <button
                      className="feature-table__toggle"
                      onClick={() => {
                        update.mutate({ id: s.id, is_active: !s.is_active });
                      }}
                      disabled={update.isPending}
                    >
                      {s.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>{' '}
                    <button
                      className="feature-table__toggle"
                      onClick={() => {
                        onDelete(s.id, s.display_name);
                      }}
                      disabled={remove.isPending}
                      aria-label={`Delete server ${s.display_name}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {remove.isError && (
            <p className="git-server-manager__error" role="alert">
              {extractErrorDetail(remove.error) ?? 'Failed to delete server.'}
            </p>
          )}
        </section>
      )}

      <GitServerRegistrationForm />
    </>
  );
}
